/**
 * useDiagnosisStatus — reads diagnosis state from the geometry detail.
 *
 * Diagnosis data now lives directly on GeometryDetailResponse:
 * - `status === 'diagnosing'` → diagnosis is running
 * - `diagnosis_at` is set → diagnosis completed
 * - `pipeline_result?.status === 'failed'` with no `diagnosis_at` → failed
 * - the latest diagnose POST was refused for a missing seat or subscription
 *   (`useDiagnosisRefusal`) → failed, with the seat/subscription sentence as
 *   `errorMessage`. The geometry row cannot say this itself: the gate refuses
 *   before it is touched, so the row stays idle with no diagnosis (ENG-3091).
 *   After a reload that POST is gone, so an idle never-diagnosed row falls
 *   back to what `GET /subscription` can prove (`useSubscriptionRefusal`).
 *
 * When complete, fetches the full diagnosis JSON from S3 via
 * GET /geometries/{id}/diagnosis/download.
 */
import { useQuery } from '@tanstack/react-query';
import {
  handleGetGeometryGeometriesGeometryIdGetOptions,
  handleDownloadDiagnosisGeometriesGeometryIdDiagnosisDownloadGetOptions,
} from '../api/generated/@tanstack/react-query.gen';
import type {
  DiagnosisResult,
  GeometryDetailResponse,
} from '@/api/generated/types.gen';
import { TIMEOUTS } from '../constants';
import { cleanFailureMessage } from '@/utils/failureMessage';
import { useEffect, useRef } from 'react';
import {
  entitlementDenialText,
  type EntitlementDenial,
} from '@/lib/entitlementDenial';
import { useLatestDiagnosePost } from './useDiagnosisRefusal';
import { useSubscriptionRefusal } from './useSubscriptionRefusal';
import { useWorkspaceReadOnly } from '@/features/workspace-readonly';

/** Diagnosis lifecycle the hook reports: running / done / failed / unknown. */
export type DiagnosisStatus = 'active' | 'completed' | 'failed' | null;

async function fetchDiagnosisFromUrl(
  url: string,
  signal?: AbortSignal,
): Promise<DiagnosisResult | null> {
  const resp = await fetch(url, { signal });
  if (!resp.ok) return null;
  try {
    return (await resp.json()) as DiagnosisResult;
  } catch {
    return null;
  }
}

/**
 * Query options for the full `DiagnosisResult` blob fetched from S3 via a
 * presigned URL.
 *
 * Extracted so the `/meshgen/$uuid` route loader can warm the same cache entry
 * this hook reads — a dashboard row click runs the loader, so meshes AND the
 * full diagnosis land before the workspace mounts instead of after. Keyed on
 * `geometryId` + `diagnosisAt` (NOT the presigned `downloadUrl`): a URL rotation
 * must not refetch, but a re-diagnosis (new `diagnosis_at`, same id) must.
 */
export function diagnosisResultQueryOptions(
  geometryId: string,
  diagnosisAt: string | null | undefined,
  downloadUrl: string,
) {
  return {
    queryKey: ['diagnosisResult', geometryId, diagnosisAt] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchDiagnosisFromUrl(downloadUrl, signal),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  };
}

/**
 * Poll interval for the geometry-detail query while diagnosis resolves, or
 * `false` to stop. Polls while diagnosis/repair is in progress, and also polls
 * an idle geometry that has no diagnosis yet (navigation can race the backend
 * starting diagnosis) — EXCEPT a Wrap upload (`geo_prep === 'ftm'`), which
 * legitimately never gets a diagnosis (ENG-991); polling it would spin forever.
 * Pure helper so the gate is unit-testable without query timers.
 */
export function diagnosisPollInterval(
  geo:
    | Pick<GeometryDetailResponse, 'status' | 'diagnosis_at' | 'geo_prep'>
    | undefined,
): number | false {
  if (!geo) return false;
  if (geo.status === 'diagnosing' || geo.status === 'repairing') {
    return TIMEOUTS.STATUS_POLL_INTERVAL;
  }
  if (geo.status === 'idle' && !geo.diagnosis_at && geo.geo_prep !== 'ftm') {
    return TIMEOUTS.STATUS_POLL_INTERVAL;
  }
  return false;
}

export function useDiagnosisStatus(geometryId: string | null) {
  // Read first: a refused POST means the idle-no-diagnosis row below is not
  // "about to start", so the poll that waits for it to start must stop (as
  // must an inferred refusal, below).
  const { refusal: postedRefusal, accepted } =
    useLatestDiagnosePost(geometryId);

  // Shares query cache with useGeometryStatus via identical query key.
  // Adds refetchInterval while geometry is being diagnosed.
  const inferredRefusalRef = useRef<EntitlementDenial | null>(null);
  const geometryQuery = useQuery({
    ...handleGetGeometryGeometriesGeometryIdGetOptions({
      path: { geometry_id: geometryId! },
    }),
    enabled: !!geometryId,
    // The reload-time inference is computed below from this query's own row,
    // so it reaches the interval through a ref.
    refetchInterval: (query) =>
      postedRefusal || inferredRefusalRef.current
        ? false
        : diagnosisPollInterval(query.state.data),
  });

  const geometry = geometryQuery.data;
  const isActive =
    geometry?.status === 'diagnosing' || geometry?.status === 'repairing';
  const isCompleted = !!geometry?.diagnosis_at;
  // The reload case: idle, never diagnosed, not a Wrap (which never is), and
  // no POST of ours this session that was refused or accepted. Not in a
  // read-only share view, whose reader's own plan says nothing about the part.
  const readOnly = useWorkspaceReadOnly();
  const inferredRefusal = useSubscriptionRefusal(
    'diagnosis',
    !readOnly &&
      geometry?.status === 'idle' &&
      !geometry.diagnosis_at &&
      geometry.geo_prep !== 'ftm' &&
      postedRefusal == null &&
      !accepted,
  );
  useEffect(() => {
    inferredRefusalRef.current = inferredRefusal;
  }, [inferredRefusal]);
  const refusal = postedRefusal ?? inferredRefusal;
  // A refusal only explains a row that has neither started nor finished; an
  // older diagnosis, or one another member started, still wins.
  const activeRefusal = !isActive && !isCompleted ? refusal : null;
  // Terminal-not-completed. `error` covers a failed diagnosis/repair AND a
  // rejected CAD-simplify candidate — both leave the geometry in `error` with no
  // `diagnosis_at`; without this they fall through to `status: null` and the rail
  // spins forever instead of resolving.
  const isFailed =
    activeRefusal != null ||
    (!isActive &&
      !isCompleted &&
      (geometry?.pipeline_result?.status === 'failed' ||
        geometry?.status === 'error'));

  // Get presigned download URL for full diagnosis JSON
  const { data: dlData } = useQuery({
    ...handleDownloadDiagnosisGeometriesGeometryIdDiagnosisDownloadGetOptions({
      path: { geometry_id: geometryId! },
    }),
    enabled: !!geometryId && isCompleted,
    staleTime: TIMEOUTS.PRESIGNED_URL_STALE,
    refetchOnWindowFocus: false,
  });

  // Fetch full diagnosis result from S3. Shares query options (and thus the
  // cache entry) with the /meshgen/$uuid route loader's diagnosis warm.
  const { data: result } = useQuery({
    ...diagnosisResultQueryOptions(
      geometryId!,
      geometry?.diagnosis_at,
      dlData?.download_url ?? '',
    ),
    enabled: !!dlData?.download_url,
  });

  // Sanitize the BE message for display: drop internal plumbing (e.g. a failed
  // repair's "… has no s3_key" on stale rows) so callers fall back to friendly
  // generic copy, and humanize leaked snake_case tokens (self_intersection →
  // self-intersection). The BE now preserves the real repair-failure reason
  // (#738), so this mainly tidies pre-fix rows + token leaks.
  //
  // A refusal's sentence is built here, at render, so it is in the active
  // locale rather than whichever one was set when the 403 arrived.
  const errorMessage = activeRefusal
    ? entitlementDenialText(activeRefusal)
    : isFailed
      ? cleanFailureMessage(geometry?.pipeline_result?.message)
      : null;

  // Typed const so the literal union survives (object-literal property
  // inference would otherwise widen it to `string`).
  const status: DiagnosisStatus = isActive
    ? 'active'
    : isCompleted
      ? 'completed'
      : isFailed
        ? 'failed'
        : null;

  return {
    status,
    isActive,
    isCompleted,
    isFailed,
    result: result ?? null,
    diagnosisJsonb: (geometry?.diagnosis ?? null) as Record<
      string,
      unknown
    > | null,
    isLoading: geometryQuery.isLoading,
    errorMessage,
    // Why diagnosis never started, when the gate refused it: lets a surface
    // that hides diagnosis (MeshGen, ENG-932) still say what is missing.
    refusal: activeRefusal,
    origin: geometry?.origin ?? null,
    filename: geometry?.filename ?? null,
    // Authoritative cavity-extraction flag from the geometry row (not the blob)
    // — drives internal-flow CFD readiness.
    hasFluidDomain: geometry?.has_fluid_domain === true,
    // Import-time geo-prep choice (ENG-990/1002): a Wrap import (`ftm`) never
    // gets a diagnosis — the backend suppresses it — so callers render an
    // explicit "no diagnosis for Wrap" state instead of a spinner that spins
    // forever. Immutable after import, so it's read straight off the row.
    isWrap: geometry?.geo_prep === 'ftm',
  };
}
